import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AnalyticsTracker from '@/components/analytics/AnalyticsTracker';
import { ThemeProvider } from '@/context/ThemeContext';
import { AppProvider } from '@/context/AppContext';
import { STNAuthProvider, useSTNAuth } from '@/context/STNAuthContext';
import { NotificationProvider } from '@/context/NotificationContext';
import AppConsentProvider from '@/components/consent/AppConsentProvider';
import { AppLayout } from '@/components/layout/AppLayout';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from '@/pages/LandingPage';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Onboarding from '@/pages/Onboarding';
import UnderReview from '@/pages/UnderReview';
import Dashboard from '@/pages/Dashboard';
import InternHiring from '@/pages/InternHiring';
import JobHiring from '@/pages/JobHiring';
import TeamUpskilling from '@/pages/TeamUpskilling';
import Events from '@/pages/Events';
import Partners from '@/pages/Partners';
import Labs from '@/pages/Labs';
import Mentors from '@/pages/Mentors';
import Notifications from '@/pages/Notifications';
import NotificationDetail from '@/pages/NotificationDetail';
import Billing from '@/pages/Billing';
import Gamification from '@/pages/Gamification';
import PartnerBatches from '@/pages/PartnerBatches';
import AssignedBatchAnalytics from '@/pages/AssignedBatchAnalytics';
import Settings from '@/pages/Settings';
import { hasFeature, hasAnyFeature } from '@/lib/featureAccess';
import { Lock, Phone } from 'lucide-react';
import './App.css';

// ── Feature gate: block a route the admin hasn't granted this startup ──────────
// `feature` = single key; `anyOf` = allow if any of these keys is granted (for
// multi-tab pages like /mentors).
const Gate = ({ feature, anyOf, children }) => {
  const { startup } = useSTNAuth();
  const allowed = anyOf ? hasAnyFeature(startup, anyOf) : hasFeature(startup, feature);
  if (allowed) return children;
  return (
    <div className="flex flex-col items-center justify-center text-center py-24 px-6">
      <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
        <Lock className="w-7 h-7 text-muted-foreground" />
      </div>
      <h2 className="text-xl font-bold text-foreground">This feature isn't enabled</h2>
      <p className="text-sm text-muted-foreground mt-1 max-w-sm">
        Your organization doesn't have access to this section yet. Contact the NexusAI team to request it.
      </p>
      <a href="tel:9205919126" className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold">
        <Phone className="w-4 h-4" /> Call NexusAI · 9205919126
      </a>
    </div>
  );
};

// ── Gate: blocks access until startup is verified ─────────────────────────────
const VerifiedGate = ({ children }) => {
  const { isAuthenticated, startup, loading } = useSTNAuth();

  if (loading) return null;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const status = startup?.status;

  if (!status || status === 'draft') return <Navigate to="/onboarding" replace />;

  if (status === 'pending_review' || status === 'rejected') return <UnderReview />;

  return children;
};

// ── Guard: prevents already-submitted users from seeing onboarding again ───────
const OnboardingGuard = ({ children }) => {
  const { isAuthenticated, startup, loading } = useSTNAuth();

  if (loading) return null;

  if (!isAuthenticated) return children;

  const status = startup?.status;

  // Already submitted or approved — send to dashboard (VerifiedGate handles UnderReview there)
  if (status === 'verified' || status === 'pending_review' || status === 'rejected') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AppRoutes = () => (
  <AppLayout>
    <Routes>
      <Route path="/dashboard"          element={<Dashboard />} />
      <Route path="/interns"            element={<Gate feature="interns_hiring"><InternHiring /></Gate>} />
      <Route path="/jobs"               element={<Gate feature="jobs_hiring"><JobHiring /></Gate>} />
      <Route path="/upskill"            element={<Gate feature="upskill"><TeamUpskilling /></Gate>} />
      <Route path="/events"             element={<Gate feature="events"><Events /></Gate>} />
      <Route path="/partners"           element={<Gate feature="partners"><Partners /></Gate>} />
      <Route path="/labs"               element={<Gate feature="labs"><Labs /></Gate>} />
      <Route path="/mentors"            element={<Gate anyOf={['mentors', 'batches_self', 'batches_mentor', 'batches_assigned']}><Mentors /></Gate>} />
      <Route path="/notifications"      element={<Notifications />} />
      <Route path="/notifications/:id"  element={<NotificationDetail />} />
      <Route path="/billing"            element={<Gate feature="billing"><Billing /></Gate>} />
      <Route path="/gamification"       element={<Gate feature="gamification"><Gamification /></Gate>} />
      <Route path="/partner-batches"    element={<Gate feature="batches_assigned"><PartnerBatches /></Gate>} />
      <Route path="/assigned-batches/:batchId" element={<Gate feature="batches_assigned"><AssignedBatchAnalytics /></Gate>} />
      <Route path="/settings"           element={<Settings />} />
      <Route path="/app/*"              element={<Navigate to="/dashboard" replace />} />
    </Routes>
  </AppLayout>
);

// Inner component so useSTNAuth() is available
const AppWithNotifications = () => {
  const { isAuthenticated } = useSTNAuth();
  return (
    <NotificationProvider isAuthenticated={isAuthenticated}>
      <AppProvider>
        {/* Wraps the routes, not just Settings: the shared consent UI is also
            used for gate dialogs at points of action, which can appear on any
            screen. */}
        <AppConsentProvider>
        <Routes>
          <Route path="/"           element={<LandingPage />} />
          <Route path="/login"      element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />
          <Route path="/onboarding" element={<OnboardingGuard><Onboarding /></OnboardingGuard>} />
          <Route path="/*"          element={
            <VerifiedGate>
              <AppRoutes />
            </VerifiedGate>
          } />
        </Routes>
        </AppConsentProvider>
      </AppProvider>
    </NotificationProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        {/* Mounted here, not inside AppLayout -- AppLayout only wraps the
            authenticated /dashboard-area routes, which left every public
            page (Login, ForgotPassword, ResetPassword, Onboarding) with no
            <Toaster/> in the tree at all: toast.error(...) calls on those
            pages (e.g. a failed login) silently went nowhere -- the real
            error was visible in the network tab but never shown to the
            user. One Toaster at the app root covers every route. */}
        <Toaster position="top-right" richColors />
        <STNAuthProvider>
          <AppWithNotifications />
        </STNAuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
